import { z } from 'zod';
import type { ProductRequest, ProductView } from '@entities/product';
import { DEFAULT_CURRENCY } from '@shared/lib/currencies';
import type { LocalePartialText, LocalizedText } from '@shared/lib/i18n-text';

const decimalString = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative decimal');

// Name is required: all three locales must be non-blank after trim.
// Description is optional: any locale may be empty; if all three are empty
// we send `null` on the wire.
const localizedRequiredSchema = z.object({
  ru: z.string().trim().min(1, 'required').max(256),
  en: z.string().trim().min(1, 'required').max(256),
  uz: z.string().trim().min(1, 'required').max(256),
});

const localizedOptionalSchema = z.object({
  ru: z.string().max(16000).default(''),
  en: z.string().max(16000).default(''),
  uz: z.string().max(16000).default(''),
});

export const productFormSchema = z
  .object({
    categoryId: z.string().uuid('select a category'),
    hsCode: z.string().trim().max(20).optional().nullable(),
    name: localizedRequiredSchema,
    description: localizedOptionalSchema,
    photos: z.array(z.string().uuid()).default([]),
    moq: z.number({ invalid_type_error: 'number expected' }).int().min(1, 'min 1'),
    priceAmount: decimalString,
    priceCurrency: z.string().regex(/^[A-Z]{3}$/, 'ISO-4217 code'),
    originCountry: z
      .string()
      .regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2')
      .optional()
      .nullable(),
    eco: z.boolean().default(false),
    ecoCertificates: z.array(z.string().uuid()).default([]),
    stock: z.boolean().default(false),
    stockDiscountPct: z
      .number({ invalid_type_error: 'number expected' })
      .int()
      .min(1)
      .max(90)
      .nullable()
      .optional(),
  })
  .refine((v) => !v.stock || (v.stockDiscountPct != null && v.stockDiscountPct >= 1), {
    message: 'discount required for stock items',
    path: ['stockDiscountPct'],
  })
  .refine((v) => !v.eco || v.ecoCertificates.length > 0, {
    message: 'eco certificates required for eco products',
    path: ['ecoCertificates'],
  });

export type ProductFormValues = z.infer<typeof productFormSchema>;

function emptyLocalized(): LocalizedText {
  return { ru: '', en: '', uz: '' };
}

export function emptyProductForm(): ProductFormValues {
  return {
    categoryId: '',
    hsCode: '',
    name: emptyLocalized(),
    description: emptyLocalized(),
    photos: [],
    moq: 1,
    priceAmount: '',
    priceCurrency: DEFAULT_CURRENCY,
    originCountry: null,
    eco: false,
    ecoCertificates: [],
    stock: false,
    stockDiscountPct: null,
  };
}

function ensureLocalized(value: LocalePartialText | null | undefined): LocalizedText {
  return {
    ru: value?.ru ?? '',
    en: value?.en ?? '',
    uz: value?.uz ?? '',
  };
}

export function productToForm(p: ProductView): ProductFormValues {
  return {
    categoryId: p.categoryId,
    hsCode: p.hsCode ?? '',
    name: ensureLocalized(p.name),
    description: ensureLocalized(p.description),
    photos: p.photos?.map((l) => l.id) ?? [],
    moq: p.moq,
    priceAmount: String(p.exportPrice.amount),
    priceCurrency: p.exportPrice.currency,
    originCountry: p.originCountry,
    eco: p.eco,
    ecoCertificates: p.ecoCertificates?.map((l) => l.id) ?? [],
    stock: p.stock,
    stockDiscountPct: p.stockDiscountPct,
  };
}

export function formToRequest(values: ProductFormValues): ProductRequest {
  // Description: drop blank locales; if all three blank → null (server stores null).
  const desc: LocalePartialText = {};
  const dRu = values.description.ru.trim();
  const dEn = values.description.en.trim();
  const dUz = values.description.uz.trim();
  if (dRu) desc.ru = dRu;
  if (dEn) desc.en = dEn;
  if (dUz) desc.uz = dUz;
  const description = Object.keys(desc).length ? desc : null;

  return {
    categoryId: values.categoryId,
    hsCode: values.hsCode?.trim() || null,
    name: {
      ru: values.name.ru.trim(),
      en: values.name.en.trim(),
      uz: values.name.uz.trim(),
    },
    description,
    photos: values.photos,
    moq: values.moq,
    priceAmount: values.priceAmount,
    priceCurrency: values.priceCurrency,
    originCountry: values.originCountry || null,
    eco: values.eco,
    ecoCertificates: values.ecoCertificates,
    stock: values.stock,
    stockDiscountPct: values.stock ? values.stockDiscountPct ?? null : null,
  };
}
